import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { classNames, useTranslate } from "helpers";
import { GetDropdownItem, initialDropdownItem } from "./consts";
import DropdownItem from "./DropdownItem";
import { TextField } from "ui/components/TextField/TextField";
import { DropdownAutocompleteProps, DropdownOption } from "./types";
import { Tooltip } from "ui/components/Tooltip";

// Pretraga + popis; procjena visine dovoljna da se odluči otvara li se prema gore.
const MENU_MAX_HEIGHT = 300;
const MENU_OFFSET = 4;

/**
 * Onemogućen <button> ne emitira hover događaje, pa tooltip odabrane vrijednosti mora stajati
 * iznad njega — inače se detalji na read-only poljima ne bi vidjeli.
 */
const DisabledTooltipWrapper: React.FC<{
  show: boolean;
  content: React.ReactNode;
  children: React.ReactNode;
}> = ({ show, content, children }) => {
  if (!show) return <>{children}</>;

  return (
    <Tooltip
      trigger={<span className="block w-full">{children}</span>}
      position="bottom-right"
      size="medium"
    >
      {content}
    </Tooltip>
  );
};

const DropdownAutocomplete: React.FC<DropdownAutocompleteProps> = (
  props: DropdownAutocompleteProps
) => {
  const {
    name,
    required,
    disabled,
    label,
    error,
    value,
    direction,
    options,
    placeholder,
    width,
    height = "normal",
    onChange,
    onSearchChange,
    onSearchFocus,
    onCreate,
    autoOpen,
    theme = "light",
    isLoading,
    inputClassName,
    onLoadMore,
    tooltip,
  } = props;

  const { t } = useTranslate();

  const [selectedItem, setSelectedItem] =
    useState<DropdownOption>(initialDropdownItem);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Popis se renderira u portalu jer ga scroll kontejneri (npr. ModalBody s overflow-y-auto)
  // inače režu; pozicija se računa iz okvira trigger elementa. Isto kao u `Dropdown`.
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updateMenuPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp =
      direction === "up" || (spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow);

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + MENU_OFFSET }
        : { top: rect.bottom + MENU_OFFSET }),
    });
  }, [direction]);

  const isError = !!error;

  const handleIsOpen = () => {
    if (!isOpen) onSearchChange("");
    setIsOpen(!isOpen);
  };

  // Popis je u portalu (izvan wrappera) — bez provjere menuRef bi se zatvorio na mousedown
  // prije nego klik na stavku uopće okine.
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;

    if (wrapperRef.current?.contains(target)) return;
    if (menuRef.current?.contains(target)) return;

    setIsOpen(false);
  }, []);

  // useLayoutEffect, ne useEffect: pozicija mora biti poznata prije prvog iscrtavanja. Inače se
  // popis na trenutak iscrta bez pozicije (na dnu stranice) i onda skoči na mjesto — trzaj.
  useLayoutEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    // Fixed pozicija ne prati scroll pretka — capture faza hvata scroll bilo kojeg kontejnera.
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  // Kad se dropdown otvori -> fokusiraj i selektuj input
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // preventScroll: fokus ne smije skrolati stranicu do polja pretrage.
      searchInputRef.current.focus({ preventScroll: true });
      searchInputRef.current.select();
    }
  }, [isOpen]);

  //automatsko otvaranje za kaskadni dropdown
  useEffect(() => {
    if (autoOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);

  useEffect(() => {
    let item = GetDropdownItem(options, value, placeholder);

    setSelectedItem(item);
  }, [options, value, placeholder]);

  const handleItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, value: string) => {
      event.preventDefault();

      let item = GetDropdownItem(options, value, placeholder);

      setSelectedItem(item);
      setIsOpen(false);
      setSearchValue("");
      onChange(event, name, item);
    },
    [onChange, setSelectedItem, setIsOpen, setSearchValue, options]
  );

  const handleSearchFocus = useCallback((e: any) => {
    onSearchFocus();
  }, []);

  // Popis se crta u portalu iznad svega (z-60), pa mora nestati prije nego se otvori
  // modal za unos — inače ostaje visjeti preko njega.
  const handleCreateClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsOpen(false);
      onCreate?.(e, searchValue);
    },
    [onCreate, searchValue, setIsOpen]
  );

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      if (!onLoadMore) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
        onLoadMore();
      }
    },
    [onLoadMore]
  );

  const handleSearchChange = useCallback((e: any) => {
    setTimeout(() => {
      // e.preventDefault();

      onSearchChange(e.target.value);
      setSearchValue(e.target.value);
    }, 500);
  }, []);

  const widthClassName = () => {
    switch (width) {
      case "min":
        return "w-24";
      case "small":
        return "w-48";
      case "medium":
        return "w-96";
      case "large":
        return "w-full";
      case "full":
        return "w-full";
      default:
        return "w-full";
    }
  };

  const heightClassName = () => {
    switch (height) {
      case "small":
        return "py-2";
      case "normal":
      default:
        return "py-3";
    }
  };

  const commonClassName = () => {
    const baseStyle = classNames("relative cursor-default rounded-sm pl-3 pr-3 text-left sm:text-sm sm:leading-6 z-0 focus:outline-none", heightClassName());

    if (disabled) {
      return classNames(baseStyle, "ring-1 ring-slate-300 bg-gray-100 text-gray-900 cursor-default", inputClassName);
    }

    if (isError) {
      return classNames(baseStyle, "ring-1 ring-red-500 bg-red-50 text-red-600 placeholder-red-700 cursor-pointer", inputClassName);
    }

    if (theme === "dark") {
      return classNames(baseStyle, "ring-1 ring-[#6b6b6b] bg-[#2a2c2f] text-white cursor-pointer", inputClassName);
    }

    return classNames(baseStyle, "ring-1 ring-slate-300 bg-slate-50 text-gray-800 placeholder-gray-400 focus:ring-slate-400 cursor-pointer", inputClassName);
  };

  const selectedItemClassName = () => {
    let result;

    if (isError && required && !disabled) {
    }
    else {
      if (theme === "light") {
        if (selectedItem.value === "") {
          result = "text-gray-400";
        }
        else {
          result = "text-gray-800";
        }
      }
    }

    return classNames("block truncate w-full pr-4 text-sm", result);
  };


  return (
    <>
      {label && (
        <label
          id="listbox-label"
          className="block text-sm mb-2 font-medium leading-6 text-[#344054]"
        >
          {t(label)}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div
        ref={wrapperRef}
        className={classNames(widthClassName(), "relative min-w-full")}
      >
        <div className={classNames(widthClassName(), "relative min-w-full")}>
          <DisabledTooltipWrapper show={!!disabled && !!tooltip} content={tooltip}>
          <button
            type="button"
            className={classNames(
              commonClassName(),
              widthClassName(),
              disabled && tooltip && "pointer-events-none",
            )}
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-labelledby="listbox-label"
            onClick={handleIsOpen}
            disabled={disabled}
          >
            <span className="flex">
              {/* Tooltip na hover odabrane stavke: ako postoji slika → na ikoni, inače → na tekstu. */}
              {selectedItem?.image && (
                tooltip ? (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                      trigger={
                        <img
                          src={selectedItem?.image}
                          alt=""
                          className="w-6 max-h-5 object-cover mr-3 flex-shrink-0 rounded-full cursor-help"
                        />
                      }
                      position="bottom-right"
                      size="medium"
                    >
                      {tooltip}
                    </Tooltip>
                  </span>
                ) : (
                  <img
                    src={selectedItem?.image}
                    alt=""
                    className="w-6 max-h-5 object-cover   mr-3 flex-shrink-0 rounded-full"
                  />
                )
              )}
              {tooltip && !selectedItem?.image && selectedItem.value ? (
                <span className="flex-1 min-w-0">
                  <Tooltip
                    trigger={
                      <span className={classNames(selectedItemClassName(), "cursor-help")}>
                        {typeof selectedItem.text === "string" ? t(selectedItem.text) : selectedItem.text}
                      </span>
                    }
                    position="bottom-right"
                    size="medium"
                  >
                    {tooltip}
                  </Tooltip>
                </span>
              ) : (
                <span className={selectedItemClassName()}>
                  {typeof selectedItem.text === "string" ? t(selectedItem.text) : selectedItem.text}
                </span>
              )}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
              {isLoading && !isOpen ? (
                <svg
                  aria-hidden="true"
                  className="w-4 h-4 text-gray-200 animate-spin fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
          </button>
          </DisabledTooltipWrapper>
          {isOpen &&
            createPortal(
              <div
                ref={menuRef}
                // Dok pozicija nije izračunata, popis se ne prikazuje.
                style={{ ...menuStyle, visibility: menuStyle.position ? undefined : "hidden" }}
                className="z-[60]"
              >
                <div className="border border-gray-300 bg-white shadow-2xl">
                <div className="relative">
                  <TextField
                    ref={searchInputRef}
                    name="search"
                    type="text"
                    placeholder={t(placeholder ?? "")}
                    onChange={handleSearchChange}
                    onClick={handleSearchFocus}
                    value={searchValue}
                    cssClassName="p-2 m-2 border-0 bg-slate-100 focus:outline-none text-sm placeholder:text-sm pr-8"
                    disableAutoFocus={false}
                  ></TextField>
                  {isLoading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <svg
                        aria-hidden="true"
                        className="w-4 h-4 text-gray-200 animate-spin fill-blue-600"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentFill"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {options && options.length > 0 && (
                  <ul
                    className={classNames(
                      direction === "up" && "bottom-12",
                      "overflow-auto max-h-56 shadow-2xl bg-white text-base focus:outline-none sm:text-sm"
                    )}
                    tabIndex={-1}
                    role="listbox"
                    aria-labelledby="listbox-label"
                    aria-activedescendant="listbox-option-3"
                    onScroll={handleListScroll}
                  >
                    {options.map((item: DropdownOption, index: number) => {
                      const prevGroup = index > 0 ? options[index - 1].group : null;
                      const showGroup = item.group && item.group !== prevGroup;
                      return (
                        <React.Fragment key={index}>
                          {showGroup && (
                            <li className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase border-t border-slate-100 first:border-t-0">
                              {item.group}
                            </li>
                          )}
                          <DropdownItem
                            text={item.text}
                            value={item.value}
                            image={item.image}
                            tooltip={item.tooltip}
                            onClick={handleItemClick}
                          />
                        </React.Fragment>
                      );
                    })}
                  </ul>
                )}
                {onCreate && searchValue && searchValue.length > 0 && (
                  <div
                    className="py-3 pl-3 text-sm cursor-pointer border-t bg-slate-100 hover:bg-blue-600 hover:text-white"
                    onClick={handleCreateClick}
                  >
                    <div className="flex items-center">
                      <div className="mr-2">
                        Kreiraj {label?.toLocaleLowerCase()}
                        {": "}
                        <strong>{searchValue}</strong>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>,
              document.body
            )}
          {isError && (
            <div className="absolute right-2 top-2/4 -translate-y-1/2 ">
              <Tooltip description={error} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(DropdownAutocomplete);
